import React, { useState } from 'react';
import { Button, Spin, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
    EBookingLimitListingState,
    IPracticeBookingLimitMember,
    IPracticeBookingLimitVo,
} from 'types/bookingLimit';
import LimitRow from './LimitRow';
import SummaryBand from './SummaryBand';
import { ConfirmSaveModal, ConfirmTurnOffModal } from './ConfirmModals';
import SectionHeader, { practicePills } from './SectionHeader';
import HistoryModal from './HistoryModal';
import { useLimitEditor } from './useLimitEditor';
import useProviderNames from './useProviderNames';
import { findRule, formatResetAt, isPausedByLimit, WEEKLY } from './helpers';
import s from './s.module.less';

interface Props {
    data?: IPracticeBookingLimitVo;
    loading: boolean;
    refetch: () => void;
}

const usedOf = (m: IPracticeBookingLimitMember): number =>
    m.usage?.find((u) => u.periodType === WEEKLY)?.usedCount ?? 0;

const PracticeLimits: React.FC<Props> = ({ data, loading, refetch }) => {
    // providerId → 姓名。只有这棵子树需要，所以在这里取，不从 Marketing 层往下穿两层
    const nameOf = useProviderNames();
    const rule = findRule(data?.rules);
    const editor = useLimitEditor(rule, refetch, true);
    const [historyOpen, setHistoryOpen] = useState(false);

    if (loading && !data) {
        return <Spin />;
    }
    if (!data) {
        return null;
    }

    const cap = rule?.limitCount ?? 0;
    const resetAt = rule ? formatResetAt(rule.periodEndAt, data.timezone) : '';
    const { summary } = data;

    /**
     * 点名用的姓名串：「Alice」/「Alice and Bob」/「Alice, Bob and Carol」。
     * 设计稿只在 tooltip 里点名（原型 pausedSentence/nearSentence），页面上不列表格。
     */
    const joinNames = (ids: number[]): string => {
        const names = ids.map(nameOf);
        if (names.length <= 1) {
            return names.join('');
        }
        return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    };

    const pausedIds = data.members.filter((m) => isPausedByLimit(m.listingState)).map((m) => m.providerId);
    // 「还差一单」只算仍在线的：已暂停的不存在「差一单」
    const nearIds = data.members
        .filter(
            (m) =>
                m.listingState === EBookingLimitListingState.ACTIVE &&
                !!rule?.enabled &&
                cap > 0 &&
                usedOf(m) === cap - 1,
        )
        .map((m) => m.providerId);

    const pausedTip =
        pausedIds.length === 1
            ? `${joinNames(pausedIds)} has reached the limit, so their profile is hidden from marketplace search until ${resetAt}.`
            : `${joinNames(pausedIds)} have reached the limit, so their profiles are hidden from marketplace search until ${resetAt}.`;
    const nearTip =
        nearIds.length === 1
            ? `${joinNames(nearIds)} is one booking from the limit. One more and their profile is hidden from marketplace search until ${resetAt}.`
            : `${joinNames(nearIds)} are one booking from the limit. One more and their profiles are hidden from marketplace search until ${resetAt}.`;

    /**
     * 不在这里重复 reset 时间：汇总带已有「Next reset」一格（原型同样没有）。
     * 触顶人数用琥珀色，是设计稿唯一在这一行用色的地方。
     */
    const usage = (
        <>
            <div className={s.usageTop}>
                <span className={`${s.usageLeft} ${pausedIds.length > 0 ? s.usageLeftAmber : ''}`}>
                    {pausedIds.length > 0 ? `${pausedIds.length} paused this week` : 'None paused'}
                    {pausedIds.length > 0 && (
                        <Tooltip title={pausedTip}>
                            <QuestionCircleOutlined className={s.usageHelp} />
                        </Tooltip>
                    )}
                </span>
            </div>
            {nearIds.length > 0 && (
                <div className={s.usageMeta}>
                    {nearIds.length === 1
                        ? '1 provider is one booking away'
                        : `${nearIds.length} providers are one booking away`}
                    <Tooltip title={nearTip}>
                        <QuestionCircleOutlined className={s.usageHelp} />
                    </Tooltip>
                </div>
            )}
        </>
    );

    const pausedCount = pausedIds.length;
    const activeCount = Math.max(0, summary.memberCount - pausedCount);
    const intro = `Limit how many new patients from the Klarity Health marketplace can book each provider per week. When a provider reaches the limit, their profile stops showing in marketplace search until ${resetAt}. This limit is separate from Pro-Listing and does not change your Pro-Listing budget or bids.${data.canEdit ? ' Only you and admins can change this.' : ''}`;

    return (
        <div>
            <SectionHeader
                pills={practicePills(activeCount, pausedCount, resetAt)}
                intro={intro}
                onViewHistory={() => setHistoryOpen(true)}
            />
            <div className={s.card}>
            <SummaryBand
                rule={rule}
                timezone={data.timezone}
                /* 后端 weeklyNewPatientCount 尚未部署到各环境时回退到本地求和 —— 两者
                   口径完全一致（各成员 WEEKLY usedCount 之和），只是算的位置不同。
                   MR !3188 上到 prod 后删掉 ?? 右边这半。 */
                bookings={
                    summary.weeklyNewPatientCount ??
                    data.members.reduce((sum, m) => sum + usedOf(m), 0)
                }
                weeklySpentCents={summary.weeklySpentCents}
                isPractice
            />

            <div className={s.body}>
                <div>
                    <div className={s.cardTitle}>Choose the practice limit</div>
                    <div className={s.cardSub}>
                        Every provider gets this limit separately, not a shared pool. Up to this many
                        new marketplace patients a week each.
                    </div>
                </div>

                <LimitRow
                    saved={rule}
                    draft={editor.draft}
                    onChange={editor.setDraft}
                    mode={data.canEdit ? 'edit' : 'readonly'}
                    errMsg={editor.errMsg}
                    perProvider
                    usage={usage}
                />

                {data.canEdit && (editor.draft.enabled || editor.dirty) && (
                    <div className={s.actions}>
                        <Button onClick={editor.reset} disabled={!editor.dirty || editor.saving}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            loading={editor.saving}
                            disabled={!editor.dirty || !!editor.errMsg}
                            onClick={editor.submit}
                        >
                            Save
                        </Button>
                    </div>
                )}
            </div>

            <div className={s.foot}>
                <span className={s.footIcon}>i</span>
                <span>
                    A booking counts when it is made; cancellations do not give the spot back. Bookings
                    made at the same moment can push the count slightly past the limit, so it is an
                    &ldquo;up to&rdquo;, not an exact stop. A limit does not guarantee that many
                    bookings.
                </span>
            </div>

            <ConfirmSaveModal
                open={editor.confirmSave}
                isPractice
                limitCount={editor.draft.limitCount ?? 0}
                willPause={editor.willPause}
                nameOf={nameOf}
                resetAt={resetAt}
                loading={editor.saving}
                onCancel={() => editor.setConfirmSave(false)}
                onConfirm={editor.commit}
            />
            <ConfirmTurnOffModal
                open={editor.confirmOff}
                isPractice
                loading={editor.saving}
                onCancel={() => editor.setConfirmOff(false)}
                onConfirm={editor.commit}
            />
            </div>
            <HistoryModal
                open={historyOpen}
                isPractice
                isGroupPractice
                onClose={() => setHistoryOpen(false)}
            />
        </div>
    );
};

export default PracticeLimits;
